import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "../../config/httpConfig";

export const initialState = {
  user: localStorage.getItem("invUser")
    ? JSON.parse(localStorage.getItem("invUser"))
    : null,
  businessTypes: [],
  designations: [],
  kycHistory: {},
  kyc: {},
  otpData: {},
  otpError: null,
  validateLoading: false,
  loading: false,
  error: null,
  userDetailsLoading: false,
  allCities: []
};

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (payload, { rejectWithValue }) => {
    try {
      const loginPayload = { ...payload, role: 'INVESTOR' };
      const response = await axios.post("/api/auth/login", loginPayload);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const loginUserBeforeOtp = createAsyncThunk(
  "auth/loginUserBeforeOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/investor/loginBeforeOtp", payload);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const loginUserAfterOtp = createAsyncThunk(
  "auth/loginUserAfterOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/investor/loginAfterOtp", payload);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const loginUserBeforeAuthenticator = createAsyncThunk(
  "auth/loginUserBeforeAuthenticator",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/investor/loginBeforeAuthenticator",
        payload
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const loginUserAfterAuthenticator = createAsyncThunk(
  "auth/loginUserAfterAuthenticator",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/investor/loginAfterAuthenticator",
        payload
      );
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const sendEmailOTP = createAsyncThunk(
  "auth/sendEmailOTP",
  async (payload, { rejectWithValue }) => {
    try {
      const email = typeof payload === 'string' ? payload : payload?.email;
      const response = await axios.post(`/api/auth/forgotPassword/sendOtp`, {
        email,
        role: "INVESTOR",
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const validateEmailOTP = createAsyncThunk(
  "auth/validateEmailOTP",
  async ({ email, otp, messageId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `/api/auth/forgotPassword/validateOtp`,
        { email, otp, messageId, role: "INVESTOR" }
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const resetPassword = createAsyncThunk(
  "auth/resetPassword",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/auth/forgotPassword/reset", payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const signupUser = createAsyncThunk(
  "auth/signupUser",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/investor/register", payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const createCastlerAccount = createAsyncThunk(
  "auth/createCastlerAccount",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        "/api/investor/createCastlerAccount",
        payload
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getAllBusinessTypes = createAsyncThunk(
  "auth/getAllBusinessTypes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/investor/getAllBusinessTypes");
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getRmCities = createAsyncThunk(
  "auth/getRmCities",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/investor/getRmCities");
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getAllDesignations = createAsyncThunk(
  "auth/getAllDesignations",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/investor/getAllDesignations");
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const kycResults = createAsyncThunk(
  "auth/kycResults",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/investor/kycResults", payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitManualKycStep = createAsyncThunk(
  "auth/submitManualKycStep",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/investor/submitManualKycStep", payload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const kycHistoryById = createAsyncThunk(
  "auth/kycHistoryById",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `/api/investor/kycHistory?orgId=${payload?.orgId}&kycTransactionId=${payload?.kycTransactionId}&kycType=${payload?.kycType}&userTypeId=${payload?.userTypeId}`
      );
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const fetchAccessToken = createAsyncThunk(
  "auth/fetchAccessToken",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get("/api/investor/generateAccessToken");
      return response.response;
    } catch (error) {
      return rejectWithValue(error || "Access token fetch failed");
    }
  }
);

export const assignToRm = createAsyncThunk(
  "auth/assignToRm",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/admin/assignToRm", payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getUserDetails = createAsyncThunk(
  "auth/getUserDetails",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/investor/getUserDetails`);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const logout = createAsyncThunk(
  "auth/logout",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/auth/logout", { role: 'INVESTOR' });
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addErrorLogs = createAsyncThunk(
  "auth/addErrorLogs",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/investor/addErrorLogs", payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const registerSendOtp = createAsyncThunk(
  "auth/registerSendOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/investor/registerSendOtp", payload);
      return response.response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const registerValidateOtp = createAsyncThunk(
  "auth/registerValidateOtp",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post("/api/investor/registerValidateOtp", payload);
      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const authSlice = createSlice({
  name: "auth",
  initialState,

  extraReducers: (builder) => {
    builder.addCase(loginUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUser.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      localStorage.setItem("invUser", JSON.stringify(action.payload));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("investor_session_active", "true");
      }
      state.error = null;
    });
    builder.addCase(loginUser.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(logout.fulfilled, (state) => {
      state.user = null;
      localStorage.removeItem("invUser");
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem("investor_session_active");
      }
    });
    builder.addCase(logout.rejected, (state) => {
      // Even if API fails, clear state so UI redirects to login
      state.user = null;
      localStorage.removeItem("invUser");
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.removeItem("investor_session_active");
      }
    });

    builder.addCase(loginUserBeforeOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserBeforeOtp.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(loginUserBeforeOtp.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(loginUserAfterOtp.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserAfterOtp.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      localStorage.setItem("invUser", JSON.stringify(action.payload));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("investor_session_active", "true");
      }
      state.error = null;
    });
    builder.addCase(loginUserAfterOtp.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(loginUserBeforeAuthenticator.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserBeforeAuthenticator.fulfilled, (state) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(loginUserBeforeAuthenticator.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(loginUserAfterAuthenticator.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(loginUserAfterAuthenticator.fulfilled, (state, action) => {
      state.loading = false;
      state.user = action.payload;
      localStorage.setItem("invUser", JSON.stringify(action.payload));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("investor_session_active", "true");
      }
      state.error = null;
    });
    builder.addCase(loginUserAfterAuthenticator.rejected, (state, action) => {
      state.loading = false;
      state.user = null;
      state.error = action.payload;
    });

    builder.addCase(sendEmailOTP.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(sendEmailOTP.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(sendEmailOTP.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(validateEmailOTP.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(validateEmailOTP.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(validateEmailOTP.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(resetPassword.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(resetPassword.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(resetPassword.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(signupUser.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(signupUser.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(signupUser.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(getAllBusinessTypes.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getAllBusinessTypes.fulfilled, (state, action) => {
      state.loading = false;
      state.businessTypes = action.payload;
    });

    builder.addCase(getRmCities.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getRmCities.fulfilled, (state, action) => {
      state.loading = false;
      state.allCities = action.payload;
    });

    builder.addCase(getAllDesignations.fulfilled, (state, action) => {
      state.designations = action.payload;
    });

    builder.addCase(kycResults.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(kycResults.fulfilled, (state, action) => {
      state.loading = false;
      state.kyc = action.payload.response;
      state.error = null;
    });
    builder.addCase(kycResults.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(submitManualKycStep.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(submitManualKycStep.fulfilled, (state, action) => {
      state.loading = false;
      state.manualKyc = action.payload;
      state.error = null;
    });
    builder.addCase(submitManualKycStep.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(kycHistoryById.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(kycHistoryById.fulfilled, (state, action) => {
      state.loading = false;
      state.kycHistory = action.payload.response;
      state.error = null;
    });
    builder.addCase(kycHistoryById.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(getUserDetails.pending, (state, action) => {
      state.userDetailsLoading = true;
      state.error = null;
    });
    builder.addCase(getUserDetails.fulfilled, (state, action) => {
      state.userDetailsLoading = false;
      state.error = null;
      state.user = action.payload.response;
      localStorage.setItem("invUser", JSON.stringify(action.payload.response));
      if (typeof sessionStorage !== "undefined") {
        sessionStorage.setItem("investor_session_active", "true");
      }
    });
    builder.addCase(getUserDetails.rejected, (state, action) => {
      state.userDetailsLoading = false;
      state.error = action.payload;
    });

    builder.addCase(addErrorLogs.pending, (state) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(addErrorLogs.fulfilled, (state, action) => {
      state.loading = false;
      state.error = null;
    });
    builder.addCase(addErrorLogs.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(registerSendOtp.pending, (state) => {
      state.otpError = null;
      state.otpData = {};
    });
    builder.addCase(registerSendOtp.fulfilled, (state, action) => {
      state.otpError = null;
      state.otpData = action.payload;
    });
    builder.addCase(registerSendOtp.rejected, (state, action) => {
      state.otpError = action.payload;
      state.otpData = {};
    });

    builder.addCase(registerValidateOtp.pending, (state) => {
      state.validateLoading = true;
      state.otpError = null;
    });
    builder.addCase(registerValidateOtp.fulfilled, (state, action) => {
      state.validateLoading = false;
      state.otpError = null;
    });
    builder.addCase(registerValidateOtp.rejected, (state, action) => {
      state.validateLoading = false;
      state.otpError = action.payload;
    });
  },
  reducers: {
    clearAuthError: (state) => {
      state.error = null;
      state.otpError = null;
    },
  },
});

export const { clearAuthError } = authSlice.actions;

export default authSlice.reducer;
